import React from 'react';
import {Link, useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';

// MUI
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
} from '@mui/material';
import Tooltip, {tooltipClasses} from '@mui/material/Tooltip';
import {styled} from '@mui/material/styles';

// Icons
import {ReactComponent as HomeIcon} from '../assets/home.svg';
import {ReactComponent as DollarIcon} from '../assets/dollar-sign-nav.svg';
import {ReactComponent as FileIcon} from '../assets/file.svg';
import {ReactComponent as GearIcon} from '../assets/gear.svg';

// Utils
import PropTypes from 'prop-types';
import {APP_HEADER_HEIGHT, GREEN_NAV} from '../App';
import {useFlags} from 'launchdarkly-react-client-sdk';

const NavTooltip = styled(({className, ...props}) => (
  <Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    color: '#fff',
    backgroundColor: '#054A41',
    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.10)',
    borderRadius: '4px',
    fontSize: 14,
    minWidth: 75,
    textAlign: 'center',
    fontWeight: 'light',
    padding: '8px',
  },
}));

const MenuItem = ({page, isCurrentPage}) => {
  return (
    <NavTooltip key={page.title} title={page.title} placement="right">
      <Box mt={1} sx={page.title === 'Settings' ? {marginTop: 'auto'} : {}}>
        <ListItem
          disablePadding
          key={page.title}
          sx={{
            width: 'auto',
            height: '60px',
            my: 1,
            mx: 2.6,
          }}
        >
          <ListItemButton
            component={Link}
            to={page.path}
            sx={{
              height: '44px',
              width: '44px',
              backgroundColor: isCurrentPage(page) ? GREEN_NAV : '#fff',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': {
                color: '#054A41',
                backgroundColor: isCurrentPage(page) ? GREEN_NAV : '#EBF0EF',
                borderRadius: '8px',
              },
            }}
          >
            <ListItemIcon sx={{minWidth: 20}}>
              <page.icon
                width={20}
                height={20}
                style={{
                  color: isCurrentPage(page) ? '#fff' : GREEN_NAV,
                  fill: 'none',
                  stroke: 'currentColor',
                }}
              />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      </Box>
    </NavTooltip>
  );
};

MenuItem.propTypes = {
  page: PropTypes.shape({
    title: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
  }).isRequired,
  isCurrentPage: PropTypes.func.isRequired,
};

const SettingsMenuItem = () => {
  return (
    <NavTooltip title="Settings" placement="right">
      <Box mt={1}>
        <ListItem
          disablePadding
          sx={{
            width: 'auto',
            height: '60px',
            my: 1,
            mx: 2.6,
          }}
        >
          <ListItemButton
            component={Link}
            to="/settings"
            sx={{
              height: '44px',
              width: '44px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': {
                color: '#054A41',
                backgroundColor: '#EBF0EF',
                borderRadius: '8px',
              },
            }}
          >
            <ListItemIcon sx={{minWidth: 20}}>
              <GearIcon
                width={20}
                height={20}
                style={{
                  color: GREEN_NAV,
                  fill: 'none',
                  stroke: 'currentColor',
                }}
              />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      </Box>
    </NavTooltip>
  );
};

export const MENU_BAR_WIDTH = 92;

const MenuBarV2 = () => {
  const {pathname} = useLocation();
  const {hasParentOrg, isExternalUser} = useSelector((state) => state.session);
  const {
    documentFolders: documentFoldersEnabled,
    financialReports: financialReportsEnabled,
    reportWorkspace: reportWorkspaceEnabled,
  } = useFlags();

  const primaryPages = [
    {
      title: 'Home',
      path: !hasParentOrg ? '/' : '/parent-organization',
      enabled: true,
      icon: HomeIcon,
    },
    !hasParentOrg &&
      !isExternalUser && {
        title: 'Statements',
        path: '/statements',
        enabled: financialReportsEnabled,
        icon: DollarIcon,
      },
    !hasParentOrg &&
      !isExternalUser && {
        title: 'Reports',
        path: documentFoldersEnabled ? '/folders' : '/documents',
        enabled: reportWorkspaceEnabled,
        icon: FileIcon,
      },
  ];

  const isCurrentPage = (page) => {
    if (page.title === 'Reports') {
      return (
        pathname === '/folders' ||
        pathname === '/documents' ||
        (pathname.startsWith('/folders/') && pathname.endsWith('/documents')) ||
        page.path === pathname
      );
    }
    return page.path === pathname;
  };

  return (
    <Drawer
      sx={{
        width: MENU_BAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          mt: APP_HEADER_HEIGHT,
          backgroundColor: '#fff',
          width: MENU_BAR_WIDTH,
          borderRadius: '0px 8px 8px 0px',
          borderRight: '1px solid #DDE0DE',
          boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.15)',
          height: `calc(100% - ${APP_HEADER_HEIGHT})`,
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <List sx={{flexGrow: 1, overflowY: 'auto'}}>
          {primaryPages
            .filter((p) => p.enabled)
            .map((page, index) => (
              <React.Fragment key={page.title}>
                <MenuItem page={page} isCurrentPage={isCurrentPage} />
              </React.Fragment>
            ))}
        </List>
        {!isExternalUser && (
          <Box mt="auto" pb={2}>
            <Divider
              sx={{
                my: 2,
                mx: 2.6,
                borderBottomWidth: 1,
                backgroundColor: '#DDE0DE',
              }}
            />

            <SettingsMenuItem />
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default MenuBarV2;
